import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PageHeader, toast } from "@optymyse/suite-ui";
import { useCompanies, useCreateContact } from "../lib/directory";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { Page, Panel } from "../components/directory/Page";
import { bodyOf, ContactForm, draftOf, type CompanyChoice } from "../components/directory/ContactForm";
import type { FormProblem } from "../components/directory/CompanyForm";

/** A company id of Link's form: one of any other is no customer of the directory's. */
const COMPANY_ID = /^cmp_[a-f0-9]{32}$/;

type NewContactSearch = { company?: string };

/** A new contact, made in the organisation's directory at Link: from a customer's page, linked to it already. */
function NewContactPage() {
  const { company } = Route.useSearch();
  const navigate = useNavigate();
  const create = useCreateContact();
  const companies = useCompanies();
  const [problem, setProblem] = useState<FormProblem | null>(null);
  const choices: CompanyChoice[] = (companies.data?.items ?? []).map((row) => ({
    id: row.id,
    name: row.display_name,
    detail: row.domain,
  }));
  return (
    <Page>
      <PageHeader
        breadcrumb={[
          { label: strings.contacts.title, href: "/admin/contacts" },
          { label: strings.contacts.form.newTitle },
        ]}
        title={strings.contacts.form.newTitle}
      />
      <Panel>
        <ContactForm
          initial={draftOf(undefined, company)}
          companies={choices}
          submitLabel={strings.contacts.form.create}
          pending={create.isPending}
          problem={problem}
          onCancel={() =>
            void (company
              ? navigate({ to: "/admin/customers/$companyId", params: { companyId: company } })
              : navigate({ to: "/admin/contacts" }))
          }
          onSubmit={(draft) => {
            setProblem(null);
            create.mutate(bodyOf(draft), {
              onSuccess: (contact) => {
                toast({ title: fill(strings.contacts.form.created, { name: contact.display_name }) });
                void navigate({ to: "/admin/contacts/$contactId", params: { contactId: contact.id } });
              },
              onError: (error) =>
                setProblem({ message: error.message, field: error instanceof ApiError ? error.field : null }),
            });
          }}
        />
      </Panel>
    </Page>
  );
}

export const Route = createFileRoute("/admin/contacts/new")({
  validateSearch: (search: Record<string, unknown>): NewContactSearch =>
    typeof search.company === "string" && COMPANY_ID.test(search.company) ? { company: search.company } : {},
  component: NewContactPage,
});
